import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';

import { IArrestCharge, IArrestee, IFile } from '@/models';

interface KaseArrestsContextProviderProps extends PropsWithChildren {
  nextRoutes: string[];
  initialArrestee?: IArrestee;
  initialEditMode?: boolean;
}

type ArrestDrawer = 'charge' | 'file' | 'description' | null;

interface KaseArrestsContextValues
  extends Omit<KaseArrestsContextProviderProps, 'children'> {
  currentArrestee?: IArrestee;
  setCurrentArrestee: Dispatch<SetStateAction<IArrestee | undefined>>;
  currentArrestCharge?: IArrestCharge;
  setCurrentArrestCharge: (newArrestCharge?: IArrestCharge) => void;
  currentArrestFile?: IFile;
  setCurrentArrestFile: (newFile?: IFile) => void;
  arrestDrawer: ArrestDrawer;
  setArrestDrawer: (newArrestDrawer: ArrestDrawer) => void;
  initialEditMode: boolean;
  setInitialEditMode: (editMode: boolean) => void;
}

const KaseArrestsContext = createContext<KaseArrestsContextValues | undefined>(
  undefined,
);

export function useKaseArrestsContext() {
  const context = useContext(KaseArrestsContext);
  if (!context) {
    throw new Error(
      'useKaseArrestsContext must be used within a KaseArrestsContextProvider',
    );
  }
  return context;
}

export function KaseArrestsContextProvider({
  children,
  initialArrestee,
  initialEditMode: initialEditModeProp = false,
  ...rest
}: KaseArrestsContextProviderProps) {
  const [currentArrestee, setCurrentArrestee] = useState<IArrestee | undefined>(
    initialArrestee,
  );
  const [currentArrestCharge, setCurrentArrestCharge] = useState<
    IArrestCharge | undefined
  >();
  const [currentArrestFile, setCurrentArrestFile] = useState<
    IFile | undefined
  >();

  const [arrestDrawer, setArrestDrawer] = useState<ArrestDrawer>(null);
  const [initialEditMode, setInitialEditMode] =
    useState<boolean>(initialEditModeProp);

  return (
    <KaseArrestsContext.Provider
      value={{
        currentArrestee,
        setCurrentArrestee,
        currentArrestCharge,
        setCurrentArrestCharge,
        currentArrestFile,
        setCurrentArrestFile,
        arrestDrawer,
        setArrestDrawer,
        initialEditMode,
        setInitialEditMode,
        ...rest,
      }}
    >
      {children}
    </KaseArrestsContext.Provider>
  );
}
